import { useMemo } from 'react'
import { useViewPresets } from './useViewPresets'
import type { ViewSettings } from './useMapBuildings'

/**
 * Merge location view settings with default view preset.
 *
 * Waits for both the location query (useMapBuildings) and presets query
 * (useViewPresets) before returning a value. This prevents the map from
 * initializing at the location's default camera and then doing a visible
 * flyTo animation to the preset position.
 *
 * @param locationId - Current location ID
 * @param locationViewSettings - ViewSettings from useMapBuildings (undefined while loading)
 * @returns Merged ViewSettings (preset camera if default exists, else location camera),
 *          or undefined while either query is still loading
 */
export function useInitialViewSettings(
  locationId: string | undefined,
  locationViewSettings: ViewSettings | undefined,
): ViewSettings | undefined {
  const { data: presets, isLoading: presetsLoading } = useViewPresets(locationId)

  return useMemo(() => {
    // Block until location view settings are ready
    if (!locationViewSettings) return undefined

    // Block until presets query resolves (loading or not yet started)
    if (presetsLoading) return undefined

    // Look for a default preset
    const defaultPreset = presets?.find((p) => p.is_default)

    if (defaultPreset) {
      return {
        center: [defaultPreset.center_lng, defaultPreset.center_lat],
        zoom: defaultPreset.zoom,
        pitch: defaultPreset.pitch,
        bearing: defaultPreset.bearing,
      }
    }

    // No default preset — fall back to location camera
    return locationViewSettings
  }, [locationViewSettings, presetsLoading, presets])
}
